import React, { useEffect, useRef } from 'react';
import { Dropdown, theme } from 'antd';
import TgAvatar from './TgAvatar';
import NameAvatar from './NameAvatar';
import TaskTag from './TaskTag';
import { taskPriorityIcons, taskNumberPriorityIcons } from './taskPriority';

import { useSelector } from 'react-redux';

import type { Task } from '../../types';
import type { RootState } from '../store';
import { useTaskSelection } from './TaskSelectionContext';

interface TodoItemProps {
    task: Task;
    provided: any;
    contentMatch: string;
    isDragging: boolean;
    onAddLink: () => void;
    onDeleteLink: (linkId: number) => void;
    onAddImage: (file: File) => void | Promise<{ imageId: number; imagePath: string } | void>;
    onDeleteImage: (imageId: number) => void;
    onEdit: () => void;
    onCopyLink: () => void;
    onAddTag: () => void;
    onDeleteTag: (index: number) => void;
    onDelete: () => void;
    onUpdateStatus: (status: number) => void;
    onShowActivities: () => void;
}

const TodoItem = (props: TodoItemProps) => {
    const {
        provided,
        task,
        contentMatch,
        isDragging,
        onEdit,
        onCopyLink,
        onDelete,
        onShowActivities,
    } = props;

    const { token: { colorBgContainer, colorText, colorBorderSecondary, borderRadius } } = theme.useToken();
    const settings = useSelector((state: RootState) => state.settings);
    const { selectedIds, draggingId, isSelected, toggleSelected } = useTaskSelection();
    const selected = isSelected(task.taskId);
    const draggingGroup = draggingId != null
        && selectedIds.includes(Number(draggingId))
        && selectedIds.length > 1;
    const isGhost = Boolean(draggingGroup && selected && !isDragging);
    const showCountBadge = Boolean(isDragging && draggingGroup);
    const suppressClickRef = useRef(false);

    useEffect(() => {
        if (isDragging) {
            suppressClickRef.current = true;
        }
    }, [isDragging]);

    // Не трогаем transition/transform — иначе @hello-pangea/dnd может «залипнуть» после drop
    const cardStyle: React.CSSProperties = {
        ...provided.draggableProps.style,
        backgroundColor: colorBgContainer,
        color: colorText,
        border: `1px solid ${selected || isDragging ? 'var(--app-primary)' : colorBorderSecondary}`,
        borderRadius,
        boxShadow: isDragging
            ? 'var(--app-shadow-md)'
            : selected
                ? '0 0 0 1px var(--app-primary)'
                : 'none',
        overflow: 'hidden',
    };

    const glassClass = settings.theme?.cardTransparency ? ' app-task-card--glass' : '';
    if (settings.theme?.cardTransparency) {
        if (settings.theme.isDark) {
            cardStyle.backgroundColor = 'rgba(0, 0, 0, 0.28)';
        } else {
            cardStyle.backgroundColor = 'rgba(255, 255, 255, 0.55)';
        }
    }

    const items = [
        {
            key: 'edit',
            label: 'Редактировать',
            onClick: onEdit,
        },
        {
            key: 'copy-link',
            label: 'Скопировать ссылку',
            onClick: onCopyLink,
        },
        {
            key: 'activities',
            label: 'История',
            onClick: onShowActivities,
        },
        {
            key: 'delete',
            label: 'Удалить',
            onClick: onDelete,
        },
    ];

    const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
        const target = event.target as HTMLElement | null;
        if (target?.closest?.('a')) {
            event.stopPropagation();
            if (!event.isTrusted) {
                event.preventDefault();
            }
            return;
        }
        if (event.altKey) {
            event.preventDefault();
            event.stopPropagation();
            toggleSelected(task.taskId);
            return;
        }
        if (suppressClickRef.current) {
            suppressClickRef.current = false;
            return;
        }
        onEdit();
    };

    const dragHandleProps = provided.dragHandleProps || {};
    const { onMouseDown: onDragMouseDown, ...restDragHandleProps } = dragHandleProps;

    const selectedClass = selected ? ' app-task-card--selected' : '';
    const ghostClass = isGhost ? ' app-task-card--selected-ghost' : '';

    return (
        <Dropdown menu={{ items }} trigger={['contextMenu']}>
            <div
                ref={provided.innerRef}
                {...provided.draggableProps}
                {...restDragHandleProps}
                className={`app-task-card${isDragging ? ' app-task-card--dragging' : ''}${glassClass}${selectedClass}${ghostClass}`}
                style={cardStyle}
                onMouseDown={(event) => {
                    if (event.altKey) {
                        event.stopPropagation();
                        return;
                    }
                    onDragMouseDown?.(event);
                }}
                onClick={handleClick}
            >
                {showCountBadge ? (
                    <span className="app-task-card__multi-badge">{selectedIds.length}</span>
                ) : null}
                <div className="app-task-card__header">
                    <div className="app-task-card__avatar">
                        {task.user ?
                            <TgAvatar user={task.user} />
                            :
                            <NameAvatar whoAddTask={task.whoAddTask} />
                        }
                    </div>
                    <div className="app-task-card__tags">
                        {task.tags && task.tags.length > 0 && (
                            task.tags.map((tag, index) => (
                                <TaskTag
                                    key={index}
                                    tag={tag}
                                />
                            ))
                        )}
                    </div>
                    {task.priority ? (
                        <div className="app-task-card__priority">
                            {settings.priorityNumberIcons
                                ? taskNumberPriorityIcons[task.priority]
                                : taskPriorityIcons[task.priority]}
                        </div>
                    ) : null}
                </div>
                <div className="app-task-card__content s-card__content">
                    <div
                        className="app-task-card__title"
                        dangerouslySetInnerHTML={{ __html: contentMatch }}
                        onMouseDown={(event) => {
                            if ((event.target as HTMLElement).closest?.('a')) {
                                event.stopPropagation();
                            }
                        }}
                        onClick={(event) => {
                            if ((event.target as HTMLElement).closest?.('a')) {
                                event.stopPropagation();
                            }
                        }}
                    />
                </div>
            </div>
        </Dropdown>
    );
};

export default TodoItem;
